import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../store';
import { fetchFollowedBuildings } from '../store/buildings-actions';
import { Building } from '../models/data';
import classes from './QuickAdd.module.scss';

// The yellow centre slot in the bottom nav. Two primary create actions:
// add a property, or write a new post. When posting, we always ask which
// property to post to if the user follows more than one — never silently
// use the "last viewed" building.

const buildingUrl = (b: Building | { id: string; plusCode?: string; addressSlug?: string } | undefined | null) => {
  if (!b) return null;
  const id = (b as any).addressSlug || ((b as any).plusCode ? (b as any).plusCode.replace(/\+/g, '-') : b.id);
  return `/building/${id}`;
};

const initial = (n?: string) => (n?.trim()?.[0] || '?').toUpperCase();

type View = 'root' | 'pickProperty';

const QuickAdd = () => {
  const dispatch = useDispatch<AppDispatch>();
  const user = useSelector((s: RootState) => s.user.user);
  const followed = useSelector((s: RootState) => s.buildings.followedBuildings);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<View>('root');
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) close();
    };
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onEsc);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Refresh the followed list when the popover opens so the picker has the
  // freshest data (in case the user added a property elsewhere).
  useEffect(() => {
    if (open && user?.id) dispatch(fetchFollowedBuildings(user.id));
  }, [open, user?.id, dispatch]);

  const close = () => { setOpen(false); setView('root'); };

  const composeAt = (b: Building) => {
    const url = buildingUrl(b);
    if (!url) return;
    close();
    navigate(`${url}?compose=1`);
  };

  const handleCreatePost = () => {
    if (followed.length === 0) {
      close();
      navigate('/my?add=1');
      return;
    }
    if (followed.length === 1) {
      composeAt(followed[0]);
      return;
    }
    setView('pickProperty');
  };

  const defaultBuildingId = user.building?.id;
  const defaultInFollowed = defaultBuildingId
    ? followed.find(b => b.id === defaultBuildingId)
    : null;

  return (
    <div className={classes.wrap} ref={wrapRef}>
      <button
        type="button"
        className={classes.trigger}
        onClick={() => { if (open) close(); else setOpen(true); }}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label="Create"
      >
        <span className={classes.trigger__plus} aria-hidden="true">+</span>
      </button>
      {open && view === 'root' && (
        <div className={classes.menu} role="menu">
          <Link
            to="/my?add=1"
            className={classes.item}
            role="menuitem"
            onClick={close}
          >
            <span className={classes.item__icon} aria-hidden="true">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 10.5 12 3l9 7.5" />
                <path d="M5 9.5V21h14V9.5" />
                <path d="M12 14v4M10 16h4" />
              </svg>
            </span>
            <span className={classes.item__body}>
              <span className={classes.item__title}>Add a property</span>
              <span className={classes.item__sub}>Find and follow an address</span>
            </span>
          </Link>
          <button
            type="button"
            className={classes.item}
            role="menuitem"
            onClick={handleCreatePost}
          >
            <span className={classes.item__icon} aria-hidden="true">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 20h9" />
                <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
              </svg>
            </span>
            <span className={classes.item__body}>
              <span className={classes.item__title}>Create a post</span>
              <span className={classes.item__sub}>
                {followed.length === 0
                  ? 'Follow a property first'
                  : followed.length === 1
                    ? `Post at ${followed[0].name}`
                    : 'Choose a property to post at'}
              </span>
            </span>
          </button>
        </div>
      )}

      {open && view === 'pickProperty' && (
        <div className={classes.menu} role="menu" aria-label="Choose a property">
          <div className={classes.pickHeader}>
            <button
              type="button"
              className={classes.pickBack}
              onClick={() => setView('root')}
              aria-label="Back"
            >
              ←
            </button>
            <span className={classes.pickTitle}>Post at…</span>
          </div>
          {defaultInFollowed && (
            <button
              type="button"
              className={classes.pickItem}
              role="menuitem"
              onClick={() => composeAt(defaultInFollowed)}
            >
              <span className={classes.pickItem__avatar} aria-hidden="true">
                {initial(defaultInFollowed.name)}
              </span>
              <span className={classes.pickItem__body}>
                <span className={classes.pickItem__name}>{defaultInFollowed.name}</span>
                <span className={classes.pickItem__meta}>★ Default</span>
              </span>
            </button>
          )}
          {followed
            .filter(b => b.id !== defaultBuildingId)
            .map(b => (
              <button
                type="button"
                key={b.id}
                className={classes.pickItem}
                role="menuitem"
                onClick={() => composeAt(b)}
              >
                <span className={classes.pickItem__avatar} aria-hidden="true">
                  {initial(b.name)}
                </span>
                <span className={classes.pickItem__body}>
                  <span className={classes.pickItem__name}>{b.name}</span>
                  {b.displayAddress && (
                    <span className={classes.pickItem__meta}>{b.displayAddress}</span>
                  )}
                </span>
              </button>
            ))}
        </div>
      )}
    </div>
  );
};

export default QuickAdd;
